import 'css/src/components/common/ui/Select/Select';

import React from 'react';
import type { ActionMeta, GroupBase, OnChangeValue, Props } from 'react-select';
import { default as ReactSelect, NonceProvider } from 'react-select';

import { customReactSelectStyles } from './custom-react-select-styles';

import CustomClearIndicator from 'js/src/components/common/ui/Select/CustomClearIndicator';
import CustomControl from 'js/src/components/common/ui/Select/CustomControl';
import CustomDropdownIndicator from 'js/src/components/common/ui/Select/CustomDropdownIndicator';
import CustomMultiValueRemove from 'js/src/components/common/ui/Select/CustomMultiValueRemove';
import CustomOption from 'js/src/components/common/ui/Select/CustomOption';
import CustomValue from 'js/src/components/common/ui/Select/CustomValue';
import FormatGroupLabel from 'js/src/components/common/ui/Select/FormatGroupLabel';
import type { SelectOption } from 'js/src/components/common/ui/Select/types';

export type SelectCustomProps<T, IsMulti extends boolean> = {
    dropUp?: boolean;
    label?: string;
    upgradeLockClicked?: (value: T) => void; // required if any options locked
    onChange?: (value: IsMulti extends true ? T[] : T) => void;
    isMulti: IsMulti extends true ? true : false; // needed for managing `onChange` type so require it to match IsMulti
    iconLeft?: string | React.ReactNode;
    isSearchable?: boolean; // default to true if more than 5 options
};

/**
 * This component is a wrapper around react-select that provides a default, customized style and functionality.
 *
 * It customizes the following:
 * - Styles (see `customReactSelectStyles` and the SCSS file)
 * - Default Dropdown indicator component (see `CustomDropdownIndicator`)
 * - Default Option component (see `CustomOption`)
 * - Default Value component (see `CustomValue`)
 * - Default Control component (see `CustomControl` which provides custom control functionality like a left icon via the `iconLeft` prop)
 * - Group label component (see `FormatGroupLabel`)
 * - Format Option Label component (see `FormatOptionLabel`)
 * - Default menu placement (see `dropUp` prop)
 * - Default max menu height (see `maxMenuHeight` prop)
 * - Supports locked options (see `upgradeLockClicked` prop)
 * - `onChange` returns the VALUE, not the full option object
 */
const Select = <
    T = unknown,
    Option extends SelectOption<T> = SelectOption<T>,
    IsMulti extends boolean = false,
    Group extends GroupBase<Option> = GroupBase<Option>,
>(
    props: Omit<Props<Option, IsMulti, Group>, 'onChange'> & SelectCustomProps<T, IsMulti>,
) => {
    const { dropUp, label, upgradeLockClicked, onChange } = props;
    let className = 'custom-select font-l5--small';
    if (props.className) {
        className += ' ' + props.className;
    }
    if (dropUp) {
        className += ' dropup';
    }

    const handleSingleChange = (newValue: Option, onChange?: (value: T) => void) => {
        // check if the option is locked – if so call upgradeLockClicked
        if (newValue.locked) {
            if (!upgradeLockClicked)
                throw new Error('upgradeLockClicked is required if any options are locked');
            upgradeLockClicked(newValue.value);
            return;
        }

        // pass through
        onChange && onChange(newValue.value);
    };

    const handleMultiChange = (newValue: Option[], onChange?: (value: T[]) => void) => {
        // check if the option is locked – if so call upgradeLockClicked
        const lockedValues = newValue.filter((value) => value.locked);
        if (lockedValues.length > 0) {
            if (!upgradeLockClicked)
                throw new Error('upgradeLockClicked is required if any options are locked');
            upgradeLockClicked(lockedValues.find((value) => value.locked)!.value);
            return;
        }

        // pass through
        onChange && onChange(newValue.map((value) => value.value));
    };

    const customOnChange = (
        newValue: OnChangeValue<Option, IsMulti>,
        actionMeta: ActionMeta<Option>,
    ) => {
        if (props.isMulti) {
            handleMultiChange(newValue as Option[], onChange as (value: T[]) => void);
        } else {
            handleSingleChange(newValue as Option, onChange as (value: T | null) => void);
        }
    };

    const defaultComponents = {
        DropdownIndicator: CustomDropdownIndicator,
        SingleValue: CustomValue<Option, IsMulti>,
        Option: CustomOption<Option>,
        ...(props.isMulti
            ? {
                  MultiValueRemove: CustomMultiValueRemove,
                  ClearIndicator: CustomClearIndicator,
              }
            : {}),
        // We need to use a custom control to render the icon if it is passed in.
        ...(props.iconLeft && { Control: CustomControl }),
    };

    // default to searchable if more than 5 options
    const isSearchable = props.isSearchable ?? (props.options && props.options.length > 5);

    return (
        <>
            {label && <label className="font-l5 font-l5--subtitle-l mb-l5-4">{label}</label>}
            {/* `react-select` dependency docs: https://react-select.com/home */}
            <NonceProvider nonce={window._CSP_NONCE} cacheKey="lumenfiveapp">
                <ReactSelect
                    isClearable={false}
                    isSearchable={isSearchable}
                    classNamePrefix="react-select"
                    menuPlacement={dropUp ? 'top' : 'bottom'}
                    backspaceRemovesValue={false}
                    styles={customReactSelectStyles}
                    maxMenuHeight={180}
                    formatGroupLabel={FormatGroupLabel}
                    {...props}
                    className={className}
                    components={{
                        // replaced components: https://react-select.com/components#replacing-components
                        ...defaultComponents,
                        ...props.components,
                    }}
                    onChange={customOnChange}
                />
            </NonceProvider>
        </>
    );
};

export default Select;
